import { useMemo } from 'react'
import { useDataSource } from '@/src/renderer/lib/dataSource'

export interface ProjectTaskProgress {
  /** Number of completed tasks (status === 'completed', not paused / deleted). */
  done: number
  /** Number of countable tasks (excludes paused / deleted). */
  total: number
  /** `done / total`, or `0` when `total === 0`. Range `[0, 1]`. */
  value: number
}

const NO_PROGRESS: ProjectTaskProgress = { done: 0, total: 0, value: 0 }

/**
 * Der zuletzt bekannte Stand je Projekt. Die Sidebar hängt ihre Zeilen beim Wechsel des
 * Lebensbereichs neu ein, und eine frische Abfrage liefert erst nach einem Takt — ohne Merker
 * stünde der Kreis kurz auf 0 und spränge dann wieder auf. Beim ersten Besuch eines Projekts
 * gibt es nichts zu merken, dort lädt es wie bisher. Gemerkt wird je Projekt ein Wert, mehr als
 * die Zahl der Projekte wird es nie.
 */
const lastKnown = new Map<string, ProjectTaskProgress>()

/**
 * Live progress for the donut indicator on a project's sidebar row.
 *
 * Counting rules (mirrors what the user sees as "open" tasks in the project):
 * - Soft-deleted tasks (`deletedAt != null`) are excluded
 * - Ruhend tasks (`pausedAt != null`) are excluded — they don't count as
 *   open *or* done, matching the Ruhend/Demnächst views
 * - `cancelled` tasks count toward `total` but NOT toward `done` (only
 *   `completed` is "done")
 * - Unteraufgaben zählen nicht: `useTasksByProject` liefert nur die oberste Ebene (T14 #179)
 *
 * Returns the last known progress of the project while the live query is loading again
 * (a row that mounts anew), `{ done: 0, total: 0, value: 0 }` on the very first load
 * or when `projectId` is missing — callers should rely on `total > 0` /
 * `value > 0` to decide visibility, not on a separate "loading" flag.
 */
export function useProjectTaskProgress(projectId: string | undefined): ProjectTaskProgress {
  const tasks = useDataSource().useTasksByProject(projectId)

  return useMemo<ProjectTaskProgress>(() => {
    if (!tasks) return (projectId && lastKnown.get(projectId)) || NO_PROGRESS
    let done = 0
    let total = 0
    for (const task of tasks) {
      if (task.deletedAt) continue
      if (task.pausedAt != null) continue
      total++
      if (task.status === 'completed') done++
    }
    const value = total > 0 ? done / total : 0
    const progress = { done, total, value }
    // Ändert sich `projectId`, hält die Abfrage kurz noch die Aufgaben des vorigen Projekts — die
    // gehören nicht unter den neuen Schlüssel.
    if (projectId && tasks.every(task => task.projectId === projectId)) lastKnown.set(projectId, progress)
    return progress
  }, [tasks, projectId])
}
